import { cloneElement, isValidElement, type ReactNode } from "react";

const NBSP = " ";

// French typography for dictionary text: curly apostrophes and a non-breaking
// space before : ; ? ! % $ €, inside « » and in numbers and units. Dictionaries
// stay readable plain text, without escape codes.
export function typo(text: string): string {
  return text
    .replace(/ /g, NBSP) // toLocaleString's narrow space isn't in Poppins.
    .replace(/'/g, "’")
    .replace(/ ([:;?!%$€»])/g, `${NBSP}$1`)
    .replace(/« /g, `«${NBSP}`)
    .replace(/(\d) (\d{3})\b/g, `$1${NBSP}$2`)
    .replace(/(\d) (M|h|min|Go)\b/g, `$1${NBSP}$2`);
}

// Values that aren't text a reader sees.
const NOT_TEXT = new Set([
  "avatarSrc",
  "avatarUrl",
  "className",
  "href",
  "poster",
  "slug",
  "sourceUrl",
  "src",
  "videoSrc",
]);

// `typo` on every string of a dictionary, including the text inside JSX.
export function typoDeep<T>(value: T): T {
  if (typeof value === "string") return typo(value) as T;
  if (Array.isArray(value)) return value.map(typoDeep) as T;
  if (isValidElement<{ children?: ReactNode }>(value)) {
    const { children } = value.props;
    if (children === undefined) return value;
    const text = typoDeep(children);
    return (
      Array.isArray(text)
        ? cloneElement(value, undefined, ...text)
        : cloneElement(value, undefined, text)
    ) as T;
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => [
        key,
        NOT_TEXT.has(key) ? item : typoDeep(item),
      ]),
    ) as T;
  }
  return value;
}
